/*
  ============================================================
  levili-motion.js — Motor de animação e UI do tema Levili (vanilla JS)
  ============================================================

  O QUE FAZ:
  Um único arquivo, sem bibliotecas, que liga os efeitos do estilo Zenin a
  partir de atributos data-* no HTML das seções:

  ANIMAÇÕES (só quando <html> tem a classe js-motion, ou seja,
  settings.enable_motion ligado; fora do editor de temas):
  - [data-split="words|chars"]  título sobe palavra a palavra / letra a letra
  - [data-reveal="up|fade|blur|scale|left|right|clip"]  revelação única
  - [data-stagger="<efeito>"]   container: filhos revelados em cascata
  - [data-count="260.91"]       contador (formato pt-BR, prefixo/sufixo)
  - [data-hotspot]              ponto -> linha desenhada -> card de vidro
  - [data-parallax="0.15"]      deslocamento ligado à rolagem
  - [data-scale-in="0.9"]       painel cresce (e arredonda menos) ao entrar
  - [data-scrub="enter|center|through"]  expõe --scrub (0..1) para o CSS
  - [data-pin]                  seção fixada com passos (explorador de categorias)

  UI (sempre, com ou sem animação):
  - [data-header]               classes is-scrolled / is-hidden no cabeçalho
  - [data-rail]                 setas anterior/próximo de carrosséis nativos
  - [data-qty]                  botões +/- de quantidade
  - [data-pin] sem animação     vira abas clicáveis (sem fixar)
  - .acc__item (<details>)      abertura suave da resposta
  - Linha dos hotspots          desenhada mesmo sem animação

  REGRAS DE OURO:
  - Estado de repouso SEMPRE visível: nada começa com opacity 0 no CSS.
    Revelações usam Web Animations API com fill: 'backwards' e só são
    criadas quando o elemento entra na tela (IntersectionObserver) ou,
    se já está na tela no carregamento, numa sequência curta.
  - Revelações animam as propriedades individuais translate/scale/opacity/
    filter (não "transform"), então não brigam com o transform do CSS.
  - Um único loop requestAnimationFrame para tudo que segue a rolagem;
    listeners passivos; pausa com a aba escondida.
  - Sem media query de "movimento reduzido" (decisão do cliente).

  API PÚBLICA (window.LeviliMotion):
  - refresh(root?)        liga elementos novos (ex.: HTML do carrinho ou da
                          Section Rendering API) e recalcula medidas.
  - reveal(el, efeito?, atraso?)  toca uma revelação agora.
  - split(el)             divide um título (sem animar).
  - drawHotspots(root?)   redesenha as linhas dos hotspots.
  - watch(el, cb)         cb(true|false) quando el entra/sai da tela OU a
                          aba muda de visibilidade (use para pausar canvas).
  - pinGoTo(secao, i)     vai para o passo i de uma seção [data-pin].
  - tick()                pede um quadro de atualização.
  - enabled / intensity   estado atual (boolean / 0.6 | 1 | 1.4).

  EVENTOS (CustomEvent, bubbles):
  - 'levili:pinstep' na seção [data-pin]:
      detail { step, prev, steps, progress, source: 'scroll'|'click'|'key'|'init' }
  - 'levili:header' no [data-header]: detail { scrolled, hidden }

  Contrato completo: levili-contract.md (scratchpad do projeto).
*/
(function () {
  'use strict';

  if (window.LeviliMotion) return;

  var doc = document;
  var html = doc.documentElement;
  var win = window;

  var MOTION = html.classList.contains('js-motion');
  var DESIGN = !!(win.Shopify && win.Shopify.designMode);
  var LEVEL = html.getAttribute('data-motion') || 'normal';
  var INT = LEVEL === 'sutil' ? 0.6 : LEVEL === 'intensa' ? 1.4 : 1;
  var DUR = LEVEL === 'sutil' ? 0.9 : LEVEL === 'intensa' ? 1.1 : 1;
  var EASE = 'cubic-bezier(.16,1,.3,1)';
  var HAS_WAAPI = typeof Element !== 'undefined' && typeof Element.prototype.animate === 'function';
  var HAS_IO = 'IntersectionObserver' in win;
  var ANIMATE = MOTION && HAS_WAAPI && HAS_IO && !DESIGN;
  var LOCALE = (win.levili && win.levili.config && win.levili.config.locale) || html.lang || 'pt-BR';

  var vh = win.innerHeight;
  var vw = win.innerWidth;
  var booted = false;

  /* Registros */
  var parallaxItems = [];
  var scaleItems = [];
  var scrubItems = [];
  var pins = [];
  var hotspots = [];
  var rails = [];
  var headerEl = null;
  var headerState = { y: 0, scrolled: false, hidden: false };

  /* ---------------------------------------------------------
     Utilitários
     --------------------------------------------------------- */
  function num(v, d) {
    var n = parseFloat(v);
    return isNaN(n) ? d : n;
  }

  function clamp(v, a, b) {
    return v < a ? a : v > b ? b : v;
  }

  function easeOut(p) {
    return 1 - Math.pow(1 - p, 3);
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function qsa(root, sel) {
    var list = toArray(root.querySelectorAll(sel));
    if (root !== doc && root.matches && root.matches(sel)) list.unshift(root);
    return list;
  }

  function emit(el, name, detail) {
    try {
      el.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }));
    } catch (e) {}
  }

  /* Toca uma animação "de" -> estado natural (keyframe implícito no fim) */
  function play(el, from, delay, duration) {
    if (!el || !HAS_WAAPI) return null;
    from.offset = 0;
    try {
      return el.animate([from], {
        duration: duration * DUR,
        delay: delay || 0,
        easing: EASE,
        fill: 'backwards'
      });
    } catch (e) {
      return null;
    }
  }

  function revealFrom(effect) {
    var d = 40 * INT;
    switch (effect) {
      case 'fade':
        return { opacity: 0 };
      case 'blur':
        return { opacity: 0, filter: 'blur(' + Math.round(14 * INT) + 'px)', translate: '0 ' + Math.round(d * 0.4) + 'px' };
      case 'scale':
        return { opacity: 0, scale: String(1 - 0.08 * INT) };
      case 'left':
        return { opacity: 0, translate: Math.round(-d * 1.2) + 'px 0' };
      case 'right':
        return { opacity: 0, translate: Math.round(d * 1.2) + 'px 0' };
      case 'clip':
        return { clipPath: 'inset(0 0 100% 0)' };
      default:
        return { opacity: 0, translate: '0 ' + Math.round(d) + 'px' };
    }
  }

  /* ---------------------------------------------------------
     One-shots (split, reveal, count, hotspot) + IntersectionObserver
     --------------------------------------------------------- */
  var oneShotIO = HAS_IO
    ? new IntersectionObserver(onOneShot, { rootMargin: '0px 0px 0px 0px', threshold: 0 })
    : null;

  function onOneShot(entries) {
    var batch = entries
      .filter(function (e) {
        return e.isIntersecting;
      })
      .map(function (e) {
        oneShotIO.unobserve(e.target);
        return e.target;
      });
    runBatch(batch, 0);
  }

  /* Executa um lote que entrou junto: itens do mesmo [data-stagger] ganham atraso crescente */
  function runBatch(list, base) {
    var groups = new Map();
    list.sort(function (a, b) {
      var ra = a.getBoundingClientRect();
      var rb = b.getBoundingClientRect();
      return ra.top - rb.top || ra.left - rb.left;
    });
    list.forEach(function (el) {
      var job = el.__lvJob;
      if (!job || job.done) return;
      job.done = true;
      var extra = 0;
      if (job.group) {
        var c = groups.get(job.group) || 0;
        extra = c * job.gap;
        groups.set(job.group, c + 1);
      }
      job.run(el, base + job.delay + extra);
    });
  }

  function register(el, job) {
    if (el.__lvJob) return;
    el.__lvJob = job;
    job.delay = job.delay || 0;
    if (!ANIMATE) return;
    var r = el.getBoundingClientRect();
    if (r.bottom <= 0 && r.height > 0) {
      job.done = true; /* já passou (página recarregada no meio) */
      return;
    }
    if (r.top < vh && r.bottom > 0) {
      /* Já está na tela: sequência de carregamento (ou imediato no refresh) */
      pendingNow.push(el);
      return;
    }
    oneShotIO.observe(el);
  }

  var pendingNow = [];

  function flushNow() {
    if (!pendingNow.length) return;
    var list = pendingNow;
    pendingNow = [];
    runBatch(list, booted ? 0 : 80);
  }

  /* data-split */
  function makeMask(text) {
    var m = doc.createElement('span');
    m.className = 'sw-mask';
    var s = doc.createElement('span');
    s.className = 'sw';
    s.textContent = text;
    m.appendChild(s);
    return m;
  }

  function splitNode(node, mode) {
    toArray(node.childNodes).forEach(function (k) {
      if (k.nodeType === 3) {
        var frag = doc.createDocumentFragment();
        k.nodeValue.split(/(\s+)/).forEach(function (part) {
          if (!part) return;
          if (/^\s+$/.test(part)) {
            frag.appendChild(doc.createTextNode(' '));
            return;
          }
          if (mode === 'chars') {
            var w = doc.createElement('span');
            w.className = 'sw-word';
            Array.from(part).forEach(function (ch) {
              w.appendChild(makeMask(ch));
            });
            frag.appendChild(w);
          } else {
            frag.appendChild(makeMask(part));
          }
        });
        node.replaceChild(frag, k);
      } else if (k.nodeType === 1 && k.tagName !== 'BR' && k.tagName !== 'SVG' && !k.classList.contains('visually-hidden')) {
        splitNode(k, mode);
      }
    });
  }

  function splitText(el) {
    if (el.__lvSplit) return;
    el.__lvSplit = true;
    var mode = el.getAttribute('data-split') === 'chars' ? 'chars' : 'words';
    var label = el.textContent.replace(/\s+/g, ' ').trim();
    var vis = doc.createElement('span');
    vis.setAttribute('aria-hidden', 'true');
    while (el.firstChild) vis.appendChild(el.firstChild);
    splitNode(vis, mode);
    if (!el.hasAttribute('aria-label')) {
      var sr = doc.createElement('span');
      sr.className = 'visually-hidden';
      sr.textContent = label;
      el.appendChild(sr);
    }
    el.appendChild(vis);
    el.classList.add('is-split');
  }

  function runSplit(el, delay) {
    var chars = el.getAttribute('data-split') === 'chars';
    var gap = num(el.getAttribute('data-split-gap'), chars ? 30 : 60);
    var dur = num(el.getAttribute('data-split-duration'), chars ? 1150 : 1000);
    toArray(el.querySelectorAll('.sw')).forEach(function (s, i) {
      var from = { opacity: 0, translate: '0 108%' };
      if (!chars) from.rotate = (5 * INT).toFixed(1) + 'deg';
      play(s, from, delay + i * gap, dur);
    });
  }

  /* data-count */
  function runCount(el, delay) {
    var raw = el.getAttribute('data-count') || '0';
    var to = num(raw, 0);
    var from = num(el.getAttribute('data-count-from'), 0);
    var decAttr = el.getAttribute('data-count-decimals');
    var dec = decAttr !== null ? parseInt(decAttr, 10) || 0 : (raw.split('.')[1] || '').length;
    var prefix = el.getAttribute('data-count-prefix') || '';
    var suffix = el.getAttribute('data-count-suffix') || '';
    var dur = num(el.getAttribute('data-count-duration'), 1600) * DUR;
    var finalText = el.textContent;
    var fmt;
    try {
      fmt = new Intl.NumberFormat(LOCALE, { minimumFractionDigits: dec, maximumFractionDigits: dec });
    } catch (e) {
      fmt = { format: function (n) { return n.toFixed(dec); } };
    }
    if (from === to) return;
    el.textContent = prefix + fmt.format(from) + suffix;
    setTimeout(function () {
      var start = 0;
      function step(t) {
        if (!start) start = t;
        var p = clamp((t - start) / dur, 0, 1);
        var e = 1 - Math.pow(1 - p, 4);
        el.textContent = prefix + fmt.format(from + (to - from) * e) + suffix;
        if (p < 1) requestAnimationFrame(step);
        else el.textContent = finalText;
      }
      requestAnimationFrame(step);
    }, delay);
  }

  /* data-reveal */
  function runReveal(el, delay) {
    var effect = el.getAttribute('data-reveal') || (el.__lvJob && el.__lvJob.effect) || 'up';
    var dur = num(el.getAttribute('data-reveal-duration'), 950);
    play(el, revealFrom(effect), delay, dur);
  }

  /* ---------------------------------------------------------
     Hotspots
     --------------------------------------------------------- */
  var hotspotRO = 'ResizeObserver' in win
    ? new ResizeObserver(function (entries) {
        entries.forEach(function (e) {
          var h = e.target.closest('.hotspot, [data-hotspot]');
          if (h) drawHotspot(h);
        });
      })
    : null;

  var hotspotVisIO = HAS_IO
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.target.__hs) {
            e.target.__hs.visible = e.isIntersecting;
            if (e.isIntersecting) requestTick();
          }
        });
      })
    : null;

  function setupHotspot(h) {
    if (h.__hs) return;
    var s = (h.__hs = {
      dot: h.querySelector('.hotspot__dot'),
      card: h.querySelector('.hotspot__card'),
      line: h.querySelector('.hotspot__line'),
      mask: h.querySelector('.hotspot__mask'),
      live: h.getAttribute('data-hotspot') === 'live',
      visible: false
    });
    hotspots.push(h);
    drawHotspot(h);
    if (hotspotRO) {
      hotspotRO.observe(h);
      if (s.card) hotspotRO.observe(s.card);
    }
    if (hotspotVisIO) hotspotVisIO.observe(h);
    if (s.card) {
      s.card.addEventListener('pointerenter', function () {
        h.classList.add('is-hover');
      });
      s.card.addEventListener('pointerleave', function () {
        h.classList.remove('is-hover');
      });
    }
    register(h, {
      delay: num(h.getAttribute('data-hotspot-delay'), 0),
      run: runHotspot
    });
  }

  function drawHotspot(h) {
    var s = h.__hs;
    if (!s || !s.dot || !s.card || !s.line) return;
    var hr = h.getBoundingClientRect();
    if (!hr.width || !h.offsetWidth) return;
    /* compensa escalas de pais (ex.: data-scale-in) */
    var sx = hr.width / h.offsetWidth || 1;
    var sy = hr.height / (h.offsetHeight || 1) || 1;
    var dr = s.dot.getBoundingClientRect();
    var cr = s.card.getBoundingClientRect();
    var x1 = dr.left + dr.width / 2 - hr.left;
    var y1 = dr.top + dr.height / 2 - hr.top;
    var l = cr.left - hr.left;
    var r = cr.right - hr.left;
    var t = cr.top - hr.top;
    var b = cr.bottom - hr.top;
    var pad = 14;
    var x2;
    var y2;
    if (x1 < l) {
      x2 = l;
      y2 = clamp(y1, t + pad, b - pad);
    } else if (x1 > r) {
      x2 = r;
      y2 = clamp(y1, t + pad, b - pad);
    } else if (y1 < t) {
      y2 = t;
      x2 = clamp(x1, l + pad, r - pad);
    } else if (y1 > b) {
      y2 = b;
      x2 = clamp(x1, l + pad, r - pad);
    } else {
      setPath(s, '');
      return;
    }
    /* começa na borda do anel, não no centro */
    var dx = x2 - x1;
    var dy = y2 - y1;
    var len = Math.sqrt(dx * dx + dy * dy) || 1;
    var ring = dr.width / 2 + 2;
    if (len <= ring + 4) {
      setPath(s, '');
      return;
    }
    x1 += (dx / len) * ring;
    y1 += (dy / len) * ring;
    var d = 'M' + (x1 / sx).toFixed(1) + ' ' + (y1 / sy).toFixed(1) + 'L' + (x2 / sx).toFixed(1) + ' ' + (y2 / sy).toFixed(1);
    setPath(s, d);
  }

  function setPath(s, d) {
    if (s.lastD === d) return;
    s.lastD = d;
    s.line.setAttribute('d', d);
    if (s.mask) s.mask.setAttribute('d', d);
  }

  function runHotspot(h, delay) {
    var s = h.__hs;
    if (!s) return;
    drawHotspot(h);
    play(s.dot, { opacity: 0, scale: '0.2' }, delay, 700);
    if (s.mask) {
      try {
        s.mask.animate([{ strokeDashoffset: 1, offset: 0 }], {
          duration: 800 * DUR,
          delay: delay + 380,
          easing: 'cubic-bezier(.65,0,.35,1)',
          fill: 'backwards'
        });
      } catch (e) {}
    }
    if (s.card) play(s.card, { opacity: 0, translate: '0 14px', scale: '0.94', filter: 'blur(8px)' }, delay + 900, 850);
  }

  function drawHotspots(root) {
    hotspots.forEach(function (h) {
      if (!root || root === doc || root.contains(h)) drawHotspot(h);
    });
  }

  /* ---------------------------------------------------------
     Pin (seção fixada com passos)
     --------------------------------------------------------- */
  function setupPin(sec) {
    if (sec.leviliPin) return;
    var indexes = qsa(sec, '[data-pin-index]').map(function (el) {
      return parseInt(el.getAttribute('data-pin-index'), 10) || 0;
    });
    var steps = parseInt(sec.getAttribute('data-pin-steps'), 10) || (indexes.length ? Math.max.apply(null, indexes) + 1 : 1);
    sec.style.setProperty('--pin-steps', steps);
    var active = sec.querySelector('[data-pin-index].is-active');
    var st = {
      sec: sec,
      steps: steps,
      step: active ? parseInt(active.getAttribute('data-pin-index'), 10) || 0 : 0,
      pinned: false,
      progress: 0
    };
    pins.push(st);

    sec.leviliPin = {
      goTo: function (i, source) {
        goToStep(st, i, source || 'click');
      },
      get step() {
        return st.step;
      },
      get steps() {
        return st.steps;
      },
      get pinned() {
        return st.pinned;
      }
    };

    sec.addEventListener('click', function (e) {
      var t = e.target.closest('[data-pin-to]');
      if (!t || !sec.contains(t)) return;
      e.preventDefault();
      goToStep(st, parseInt(t.getAttribute('data-pin-to'), 10) || 0, 'click');
    });

    sec.addEventListener('keydown', function (e) {
      var t = e.target.closest('[data-pin-to]');
      if (!t) return;
      var k = e.key;
      var dir = k === 'ArrowDown' || k === 'ArrowRight' ? 1 : k === 'ArrowUp' || k === 'ArrowLeft' ? -1 : 0;
      var i;
      if (k === 'Home') i = 0;
      else if (k === 'End') i = st.steps - 1;
      else if (dir) i = clamp(st.step + dir, 0, st.steps - 1);
      else return;
      e.preventDefault();
      goToStep(st, i, 'key');
      var next = sec.querySelector('[data-pin-to="' + i + '"]');
      if (next) next.focus();
    });

    setStep(st, st.step, 'init', true);
    updatePinMode(st);
  }

  function updatePinMode(st) {
    var want = MOTION && vw >= 1024 && st.sec.getAttribute('data-pin') !== 'off';
    if (want !== st.pinned) {
      st.pinned = want;
      st.sec.classList.toggle('is-pinned', want);
    }
  }

  function setStep(st, i, source, force) {
    i = clamp(i, 0, st.steps - 1);
    if (i === st.step && !force) return;
    var prev = st.step;
    st.step = i;
    st.sec.setAttribute('data-step', i);
    qsa(st.sec, '[data-pin-index]').forEach(function (el) {
      var on = parseInt(el.getAttribute('data-pin-index'), 10) === i;
      el.classList.toggle('is-active', on);
    });
    qsa(st.sec, '[data-pin-to]').forEach(function (el) {
      var on = parseInt(el.getAttribute('data-pin-to'), 10) === i;
      el.classList.toggle('is-active', on);
      if (el.getAttribute('role') === 'tab') {
        el.setAttribute('aria-selected', on ? 'true' : 'false');
        el.setAttribute('tabindex', on ? '0' : '-1');
      }
    });
    emit(st.sec, 'levili:pinstep', {
      step: i,
      prev: prev,
      steps: st.steps,
      progress: st.progress,
      source: source
    });
  }

  function goToStep(st, i, source) {
    i = clamp(i, 0, st.steps - 1);
    if (st.pinned) {
      var r = st.sec.getBoundingClientRect();
      var total = r.height - vh;
      var top = win.pageYOffset + r.top + ((i + 0.5) / st.steps) * total;
      win.scrollTo({ top: Math.round(top), behavior: 'smooth' });
      setStep(st, i, source);
    } else {
      setStep(st, i, source);
    }
  }

  function updatePin(st) {
    if (!st.pinned) return;
    var r = st.sec.getBoundingClientRect();
    var total = r.height - vh;
    if (total <= 0) return;
    var p = clamp(-r.top / total, 0, 1);
    st.progress = p;
    var i = Math.min(st.steps - 1, Math.floor(p * st.steps));
    st.sec.style.setProperty('--pin-progress', p.toFixed(4));
    st.sec.style.setProperty('--pin-local', (p * st.steps - i).toFixed(4));
    if (r.top <= vh && r.bottom >= 0) setStep(st, i, 'scroll');
  }

  /* ---------------------------------------------------------
     Parallax / scale-in / scrub (loop de rolagem)
     --------------------------------------------------------- */
  function setupParallax(el) {
    if (el.__lvPar) return;
    el.__lvPar = {
      el: el,
      f: num(el.getAttribute('data-parallax'), 0.1),
      fx: num(el.getAttribute('data-parallax-x'), 0),
      rot: num(el.getAttribute('data-parallax-rotate'), 0),
      y: 0,
      x: 0
    };
    parallaxItems.push(el.__lvPar);
  }

  function setupScale(el) {
    if (el.__lvScale) return;
    var cs = win.getComputedStyle(el);
    el.__lvScale = {
      el: el,
      from: num(el.getAttribute('data-scale-in'), 0.9),
      r0: num(el.getAttribute('data-scale-radius'), 56),
      r1: parseFloat(cs.borderTopLeftRadius) || 0,
      s: 1
    };
    scaleItems.push(el.__lvScale);
  }

  function setupScrub(el) {
    if (el.__lvScrub) return;
    el.__lvScrub = { el: el, mode: el.getAttribute('data-scrub') || 'enter', last: -1 };
    scrubItems.push(el.__lvScrub);
  }

  function updateScroll() {
    var mobile = vw < 768;
    var i;
    var it;
    var r;

    /* LEITURA */
    for (i = 0; i < parallaxItems.length; i++) {
      it = parallaxItems[i];
      r = it.el.getBoundingClientRect();
      it.cy = r.top + r.height / 2 - it.y;
      it.inView = r.bottom > -vh * 0.5 && r.top < vh * 1.5;
    }
    for (i = 0; i < scaleItems.length; i++) {
      it = scaleItems[i];
      r = it.el.getBoundingClientRect();
      var h0 = r.height / (it.s || 1);
      it.top = r.top + r.height / 2 - h0 / 2;
    }
    for (i = 0; i < scrubItems.length; i++) {
      it = scrubItems[i];
      r = it.el.getBoundingClientRect();
      it.r = r;
    }

    /* ESCRITA */
    for (i = 0; i < parallaxItems.length; i++) {
      it = parallaxItems[i];
      if (!it.inView) continue;
      var delta = it.cy - vh / 2;
      var k = INT * (mobile ? 0.5 : 1);
      it.y = -delta * it.f * k;
      it.x = -delta * it.fx * k;
      var rot = it.rot ? ' rotate(' + ((delta / vh) * it.rot * k).toFixed(2) + 'deg)' : '';
      it.el.style.transform = 'translate3d(' + it.x.toFixed(1) + 'px,' + it.y.toFixed(1) + 'px,0)' + rot;
    }
    for (i = 0; i < scaleItems.length; i++) {
      it = scaleItems[i];
      var p = easeOut(clamp((vh - it.top) / (vh * 0.75), 0, 1));
      var s = it.from + (1 - it.from) * p;
      if (Math.abs(s - it.s) < 0.0005 && it.done) continue;
      it.s = s;
      it.done = true;
      it.el.style.scale = s.toFixed(4);
      it.el.style.borderRadius = (it.r0 + (it.r1 - it.r0) * p).toFixed(1) + 'px';
    }
    for (i = 0; i < scrubItems.length; i++) {
      it = scrubItems[i];
      r = it.r;
      var v;
      if (it.mode === 'center') v = (vh - r.top) / ((vh + r.height) / 2);
      else if (it.mode === 'through') v = (vh - r.top) / (vh + r.height);
      else v = (vh - r.top) / (Math.min(r.height, vh) * 0.85);
      v = clamp(v, 0, 1);
      if (Math.abs(v - it.last) < 0.001) continue;
      it.last = v;
      it.el.style.setProperty('--scrub', v.toFixed(3));
    }
  }

  /* ---------------------------------------------------------
     Cabeçalho
     --------------------------------------------------------- */
  function updateHeader(y) {
    if (!headerEl || !headerEl.isConnected) {
      headerEl = doc.querySelector('[data-header]');
      if (!headerEl) return;
    }
    var st = headerState;
    var scrolled = y > 24;
    var hidden = st.hidden;
    var hh = headerEl.offsetHeight || 76;
    if (y < 10) hidden = false;
    else if (y > st.y + 6 && y > hh * 2) hidden = true;
    else if (y < st.y - 6) hidden = false;
    if (
      html.classList.contains('is-locked') ||
      headerEl.hasAttribute('data-header-keep') ||
      headerEl.contains(doc.activeElement)
    ) {
      hidden = false;
    }
    st.y = y;
    if (scrolled !== st.scrolled || hidden !== st.hidden) {
      st.scrolled = scrolled;
      st.hidden = hidden;
      headerEl.classList.toggle('is-scrolled', scrolled);
      headerEl.classList.toggle('is-hidden', hidden);
      html.classList.toggle('header-scrolled', scrolled);
      html.classList.toggle('header-hidden', hidden);
      emit(headerEl, 'levili:header', { scrolled: scrolled, hidden: hidden });
    }
  }

  /* ---------------------------------------------------------
     Loop único
     --------------------------------------------------------- */
  var ticking = false;

  function requestTick() {
    if (ticking || doc.hidden) return;
    ticking = true;
    requestAnimationFrame(frame);
  }

  function frame() {
    ticking = false;
    var y = win.pageYOffset;
    updateHeader(y);
    if (MOTION) updateScroll();
    for (var i = 0; i < pins.length; i++) updatePin(pins[i]);
    var live = false;
    for (var j = 0; j < hotspots.length; j++) {
      var s = hotspots[j].__hs;
      if (s && s.live && s.visible) {
        drawHotspot(hotspots[j]);
        live = true;
      }
    }
    if (live) requestTick();
  }

  /* ---------------------------------------------------------
     Carrossel [data-rail]
     --------------------------------------------------------- */
  function setupRail(rail) {
    if (rail.__lvRail) return;
    var track = rail.querySelector('[data-rail-track]') || rail.querySelector('.rail__track');
    if (!track) return;
    var prev = rail.querySelector('[data-rail-prev]');
    var next = rail.querySelector('[data-rail-next]');
    var center = rail.getAttribute('data-rail') === 'center';
    var st = (rail.__lvRail = { rail: rail, track: track, prev: prev, next: next, center: center, raf: 0 });
    rails.push(st);

    function stepSize() {
      var first = track.children[0];
      if (!first) return track.clientWidth * 0.8;
      var gap = parseFloat(win.getComputedStyle(track).columnGap) || 0;
      return first.getBoundingClientRect().width + gap;
    }

    function go(dir) {
      track.scrollBy({ left: dir * stepSize(), behavior: 'smooth' });
    }

    if (prev) prev.addEventListener('click', function () { go(-1); });
    if (next) next.addEventListener('click', function () { go(1); });

    track.addEventListener(
      'scroll',
      function () {
        if (st.raf) return;
        st.raf = requestAnimationFrame(function () {
          st.raf = 0;
          updateRail(st);
        });
      },
      { passive: true }
    );
    updateRail(st);
  }

  function updateRail(st) {
    var t = st.track;
    var max = t.scrollWidth - t.clientWidth - 2;
    if (st.prev) st.prev.disabled = t.scrollLeft <= 2;
    if (st.next) st.next.disabled = t.scrollLeft >= max;
    if (st.center) {
      var tr = t.getBoundingClientRect();
      var mid = tr.left + tr.width / 2;
      var best = null;
      var bestD = Infinity;
      toArray(t.children).forEach(function (c) {
        var r = c.getBoundingClientRect();
        var d = Math.abs(r.left + r.width / 2 - mid);
        if (d < bestD) {
          bestD = d;
          best = c;
        }
      });
      toArray(t.children).forEach(function (c) {
        c.classList.toggle('is-center', c === best);
      });
    }
  }

  /* ---------------------------------------------------------
     UI delegada: quantidade e acordeão
     --------------------------------------------------------- */
  doc.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-qty-minus], [data-qty-plus]');
    if (!btn) return;
    var wrap = btn.closest('[data-qty]');
    var input = wrap && wrap.querySelector('input');
    if (!input) return;
    var stepV = num(input.step, 1) || 1;
    var min = input.min !== '' ? num(input.min, 0) : 0;
    var max = input.max !== '' ? num(input.max, Infinity) : Infinity;
    var val = num(input.value, min);
    var nv = clamp(val + (btn.hasAttribute('data-qty-plus') ? stepV : -stepV), min, max);
    if (nv === val) return;
    input.value = nv;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  doc.addEventListener(
    'toggle',
    function (e) {
      var d = e.target;
      if (!ANIMATE || !d.open || !d.classList || !d.classList.contains('acc__item')) return;
      var a = d.querySelector('.acc__a');
      if (a) play(a, { opacity: 0, translate: '0 -8px' }, 0, 550);
    },
    true
  );

  /* ---------------------------------------------------------
     watch(): visibilidade de um elemento + da aba
     --------------------------------------------------------- */
  function watch(el, cb) {
    var inView = false;
    var last = null;
    function report() {
      var v = inView && !doc.hidden;
      if (v !== last) {
        last = v;
        cb(v);
      }
    }
    if (HAS_IO) {
      new IntersectionObserver(function (entries) {
        inView = entries[entries.length - 1].isIntersecting;
        report();
      }).observe(el);
    } else {
      inView = true;
      report();
    }
    doc.addEventListener('visibilitychange', report);
    return report;
  }

  /* ---------------------------------------------------------
     Ligação (bind) e refresh
     --------------------------------------------------------- */
  function bind(root) {
    root = root || doc;
    headerEl = doc.querySelector('[data-header]');

    if (ANIMATE) {
      qsa(root, '[data-split]').forEach(function (el) {
        if (el.__lvJob) return;
        splitText(el);
        register(el, { delay: num(el.getAttribute('data-reveal-delay'), 0), run: runSplit });
      });

      qsa(root, '[data-stagger]').forEach(function (box) {
        if (box.__lvStagger) return;
        box.__lvStagger = true;
        var effect = box.getAttribute('data-stagger') || 'up';
        var gap = num(box.getAttribute('data-stagger-gap'), 80);
        var marked = toArray(box.querySelectorAll('[data-stagger-item]'));
        var items = marked.length ? marked : toArray(box.children);
        items.forEach(function (el) {
          if (el.__lvJob || el.hidden) return;
          register(el, {
            group: box,
            gap: gap,
            effect: effect,
            delay: num(el.getAttribute('data-reveal-delay'), 0),
            run: runReveal
          });
        });
      });

      qsa(root, '[data-reveal]').forEach(function (el) {
        if (el.__lvJob) return;
        register(el, { delay: num(el.getAttribute('data-reveal-delay'), 0), run: runReveal });
      });

      qsa(root, '[data-count]').forEach(function (el) {
        if (el.__lvJob) return;
        register(el, { delay: num(el.getAttribute('data-reveal-delay'), 0), run: runCount });
      });
    }

    qsa(root, '[data-hotspot]').forEach(setupHotspot);

    if (MOTION) {
      qsa(root, '[data-parallax]').forEach(setupParallax);
      qsa(root, '[data-scale-in]').forEach(setupScale);
      qsa(root, '[data-scrub]').forEach(setupScrub);
    }

    qsa(root, '[data-pin]').forEach(setupPin);
    qsa(root, '[data-rail]').forEach(setupRail);

    flushNow();
  }

  function prune() {
    function alive(it) {
      return (it.el || it.sec || it.rail).isConnected;
    }
    parallaxItems = parallaxItems.filter(alive);
    scaleItems = scaleItems.filter(alive);
    scrubItems = scrubItems.filter(alive);
    pins = pins.filter(alive);
    rails = rails.filter(alive);
    hotspots = hotspots.filter(function (h) {
      return h.isConnected;
    });
  }

  function refresh(root) {
    prune();
    vh = win.innerHeight;
    vw = win.innerWidth;
    bind(root || doc);
    pins.forEach(updatePinMode);
    rails.forEach(updateRail);
    drawHotspots(root);
    requestTick();
  }

  var resizeTimer = 0;
  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      var w = win.innerWidth;
      var widthChanged = w !== vw;
      vh = win.innerHeight;
      vw = w;
      if (widthChanged) {
        pins.forEach(updatePinMode);
        scaleItems.forEach(function (it) {
          it.el.style.borderRadius = '';
          it.el.style.scale = '';
          it.s = 1;
          it.r1 = parseFloat(win.getComputedStyle(it.el).borderTopLeftRadius) || 0;
          it.done = false;
        });
      }
      rails.forEach(updateRail);
      drawHotspots();
      requestTick();
    }, 120);
  }

  function init() {
    bind(doc);
    booted = true;
    frame();

    win.addEventListener('scroll', requestTick, { passive: true });
    win.addEventListener('resize', onResize, { passive: true });
    win.addEventListener('load', function () {
      drawHotspots();
      requestTick();
    });
    doc.addEventListener('visibilitychange', function () {
      if (!doc.hidden) requestTick();
    });
    if (doc.fonts && doc.fonts.ready) {
      doc.fonts.ready.then(function () {
        drawHotspots();
        requestTick();
      });
    }

    /* Editor de temas do Shopify */
    doc.addEventListener('shopify:section:load', function (e) {
      refresh(e.target);
    });
    doc.addEventListener('shopify:section:unload', function () {
      setTimeout(prune, 0);
    });
  }

  win.LeviliMotion = {
    version: '1.0.0',
    enabled: MOTION,
    intensity: INT,
    refresh: refresh,
    reveal: function (el, effect, delay) {
      if (!el || !ANIMATE) return null;
      return play(el, revealFrom(effect || el.getAttribute('data-reveal') || 'up'), delay || 0, 950);
    },
    split: function (el) {
      if (el) splitText(el);
    },
    drawHotspots: drawHotspots,
    watch: watch,
    pinGoTo: function (sec, i) {
      if (sec && sec.leviliPin) sec.leviliPin.goTo(i);
    },
    tick: requestTick
  };

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
